import { Controller } from "@hotwired/stimulus"

// The contact card behind a name on a message. One panel on the body, same
// reason message_menu cannot reuse dropdown_controller: the cards live inside
// .thread__scroll, which would clip a descendant menu.
const VIEWPORT_MARGIN = 8
const TRIGGER_GAP = 6
const TRIGGER_SELECTOR = "[data-contact-popover]"

const COMPOSE_REASONS = {
  slack: "Slack replies go to the thread, not to a person.",
  "no-email": "No email address to write to."
}

export default class extends Controller {
  static targets = [ "initials", "name", "email", "copyName", "copyEmail",
                     "compose", "composeDivider" ]

  connect() {
    this.openFromEvent = this.openFromEvent.bind(this)
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.closeOnScroll = this.closeOnScroll.bind(this)

    document.addEventListener("click", this.openFromEvent)
    document.addEventListener("keydown", this.closeOnEscape)
    document.addEventListener("scroll", this.closeOnScroll, true)
  }

  disconnect() {
    this.close()
    document.removeEventListener("click", this.openFromEvent)
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("scroll", this.closeOnScroll, true)
  }

  openFromEvent(event) {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR)
    if (trigger) {
      event.preventDefault()
      this.toggle(trigger)
      return
    }

    if (!this.element.hidden && !this.element.contains(event.target)) this.close()
  }

  toggle(trigger) {
    if (this.trigger === trigger && !this.element.hidden) {
      this.close()
      return
    }

    this.open(trigger)
  }

  open(trigger) {
    this.trigger = trigger
    this.fill(trigger)
    this.element.hidden = false
    this.position(trigger)
    this.markExpanded(true)
    this.ignoreScroll = true
    this.firstAction?.focus({ preventScroll: true })
    requestAnimationFrame(() => { this.ignoreScroll = false })
  }

  close() {
    if (this.element.hidden) return

    this.element.hidden = true
    this.markExpanded(false)
    this.trigger = null
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || this.element.hidden) return

    const trigger = this.trigger
    this.close()
    trigger?.focus({ preventScroll: true })
  }

  closeOnScroll() {
    if (this.ignoreScroll) return

    this.close()
  }

  compose() {
    const trigger = this.trigger
    const email = trigger?.dataset.contactPopoverEmail
    this.close()
    trigger?.focus({ preventScroll: true })
    if (!email) return

    this.replyComposer(trigger)?.composeFromMessage({ to: email, cc: "" })
  }

  // Written with textContent, never innerHTML: the name and address came off
  // a message header, and nothing here should be able to become markup.
  fill(trigger) {
    const name = trigger.dataset.contactPopoverName || ""
    const email = trigger.dataset.contactPopoverEmail || ""
    const initials = trigger.dataset.contactPopoverInitials || ""
    const canCopyName = trigger.dataset.contactPopoverCanCopyName === "true"
    const canCopyEmail = trigger.dataset.contactPopoverCanCopyEmail === "true"
    const canCompose = trigger.dataset.contactPopoverCanCompose === "true"
    const showCompose = trigger.dataset.contactPopoverShowCompose === "true"
    const reason = trigger.dataset.contactPopoverComposeReason || ""

    this.initialsTarget.textContent = initials
    this.nameTarget.textContent = name
    this.emailTarget.textContent = email
    this.emailTarget.hidden = !email
    this.emailTarget.title = email
    this.element.setAttribute("aria-label", name || email || "Contact")

    this.wireCopy(this.copyNameTarget, canCopyName ? name : "", "Copy name")
    this.wireCopy(this.copyEmailTarget, canCopyEmail ? email : "", "Copy email")

    this.composeTarget.hidden = !showCompose
    this.composeDividerTarget.hidden = !showCompose
    this.composeTarget.disabled = !canCompose
    this.composeTarget.title = canCompose ? "" : (COMPOSE_REASONS[reason] || "")
  }

  wireCopy(wrapper, value, restLabel) {
    wrapper.hidden = !value
    if (!value) return

    const field = this.application.getControllerForElementAndIdentifier(wrapper, "copy-field")
    if (!field) return

    field.urlValue = value
    field.restLabelValue = restLabel
    if (field.hasUrlTarget) field.urlTarget.value = value
    field.reportAtRest()
  }

  position(trigger) {
    const anchor = trigger.getBoundingClientRect()
    const panel = this.element.getBoundingClientRect()

    const below = anchor.bottom + TRIGGER_GAP
    const top = below + panel.height > window.innerHeight - VIEWPORT_MARGIN
      ? anchor.top - panel.height - TRIGGER_GAP
      : below

    const rightmost = window.innerWidth - panel.width - VIEWPORT_MARGIN
    const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, rightmost))

    this.element.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`
    this.element.style.left = `${left}px`
  }

  markExpanded(open) {
    document.querySelectorAll(`${TRIGGER_SELECTOR}[aria-expanded]`).forEach((button) => {
      button.setAttribute("aria-expanded", button === this.trigger && open ? "true" : "false")
    })
  }

  replyComposer(trigger) {
    const root = trigger?.closest(".thread")?.querySelector("#reply_composer")
    const form = root?.querySelector("[data-controller~='composer']") || root?.querySelector("form")
    if (!form) return null

    return this.application.getControllerForElementAndIdentifier(form, "composer")
  }

  get firstAction() {
    const copyName = this.copyNameTarget.hidden
      ? null
      : this.copyNameTarget.querySelector("[data-copy-field-target='button']")
    if (copyName) return copyName

    const copyEmail = this.copyEmailTarget.hidden
      ? null
      : this.copyEmailTarget.querySelector("[data-copy-field-target='button']")
    if (copyEmail) return copyEmail

    if (!this.composeTarget.hidden && !this.composeTarget.disabled) return this.composeTarget
    return null
  }
}
